<template>
  <popup-modal ref="popup">
    <h3>Resync</h3>
    <p class="mt-3">Terminal: <strong>{{ terminalId }}</strong> ({{ deviceName }})</p>
    <p>
      Terminalen bygger forbindelsen til Sync Gateway på nytt og henter alle dokumenter (produkter, kategorier, ordre)
      på nytt. Den får beskjeden neste gang den tar kontakt, innen 30 sekunder, og her ser du når den er koblet til igjen.
    </p>

    <!-- Before TastyPOS 1.6.33 a resync could close a working connection and never reconnect (2026-10-16,
         mollapizza KASSE 1: the reboot had fixed it, the resync broke it again). A terminal on such a version
         reports nothing, which is how it is recognised here. -->
    <div v-if="!phase && oldApp" class="alert alert-warning">
      Denne terminalen kjører <strong>{{ syncState.appVersion || 'en eldre app-versjon' }}</strong>. I den versjonen kan
      Resync lukke en forbindelse som virker, uten å koble til igjen. Start terminalen på nytt fra MDM i stedet, eller
      oppdater appen først.
    </div>

    <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>
    <div v-if="phaseMessage" class="alert" :class="phaseClass">
      <span v-if="waiting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      {{ phaseMessage }}
    </div>

    <div class="btn-panel">
      <button class="bo_btn btn-width" @click="cancel">{{ phase ? 'Lukk' : 'Avbryt' }}</button>
      <button v-if="!phase" class="bo_btn btn-wide ms-3" @click="confirm" :disabled="saving">
        {{ saving ? 'Sender...' : (oldApp ? 'Send likevel' : 'Bekreft') }}
      </button>
    </div>
  </popup-modal>
</template>

<script>
import PopupModal from '@/components/other/PopupModal.vue'
import TerminalService from '@/services/terminal/TerminalService'

/** How often the dialog asks how the resync is going, and for how long before it says what it knows. */
const POLL_MS = 5000
const GIVE_UP_MS = 3 * 60 * 1000
/** The device reports in whole seconds; allow for that when comparing its times with the server's. */
const SLACK_MS = 5000

export default {
  name: 'ForceResyncModal',
  components: { PopupModal },
  emits: ['closed'],
  data() {
    return {
      documentId: '',
      terminalId: '',
      deviceName: '',
      syncState: null,
      errorMessage: '',
      saving: false,
      // '' (not sent) → 'sent' → 'delivered' → 'connected'; or 'legacy', 'stuck', 'undelivered' when it ends there.
      phase: '',
      requestedAt: '',
      pollTimer: null,
      pollStartedAt: 0
    }
  },
  computed: {
    oldApp() {
      return !!(this.syncState && this.syncState.known && this.syncState.reports === false)
    },
    waiting() {
      return this.phase === 'sent' || this.phase === 'delivered'
    },
    phaseMessage() {
      switch (this.phase) {
        case 'sent':
          return 'Sendt. Venter på at terminalen tar kontakt og henter beskjeden …'
        case 'delivered':
          return 'Terminalen har fått beskjeden og kobler til Sync Gateway på nytt …'
        case 'connected':
          return 'Ferdig: terminalen er koblet til Sync Gateway igjen og henter alle dokumenter på nytt.'
        case 'legacy':
          return 'Terminalen har fått beskjeden. Denne app-versjonen sier ikke fra om den kom tilbake — se Synk-kolonnen, '
            + 'og start den på nytt fra MDM hvis den ikke synker.'
        case 'stuck':
          return 'Terminalen har fått beskjeden, men har ikke meldt at den er koblet til igjen etter 3 minutter. Den prøver '
            + 'selv videre. Er den fortsatt ikke tilkoblet om noen minutter, start den på nytt fra MDM.'
        case 'undelivered':
          return 'Terminalen har ikke hentet beskjeden på 3 minutter, så den har ikke vært i kontakt med serveren. Er den '
            + 'slått på og på nett? Beskjeden ligger klar til neste gang den tar kontakt.'
        default:
          return ''
      }
    },
    phaseClass() {
      if (this.phase === 'connected') return 'alert-success'
      if (this.phase === 'stuck' || this.phase === 'undelivered') return 'alert-warning'
      return 'alert-info'
    }
  },
  beforeUnmount() {
    this.stopPolling()
  },
  methods: {
    open(documentId, terminalId, deviceName, syncState) {
      this.stopPolling()
      this.documentId = documentId
      this.terminalId = terminalId
      this.deviceName = deviceName || ''
      this.syncState = syncState || null
      this.errorMessage = ''
      this.saving = false
      this.phase = ''
      this.requestedAt = ''
      this.$refs.popup.open()
    },
    cancel() {
      this.stopPolling()
      this.$refs.popup.close()
      this.$emit('closed')
    },
    confirm() {
      this.errorMessage = ''
      this.saving = true
      TerminalService.forceResync(this.documentId).then(
        (response) => {
          this.saving = false
          // HTTP 200 is not success here: the server answers {status: "FAILED", message} in a 200, and a request the
          // security layer refuses comes back as an empty 200. Only SUCCEEDED means the flag was set (2026-10-16:
          // «Resync er planlagt» was shown while nothing reached the server).
          const data = response && response.data ? response.data : {}
          if (data.status === 'SUCCEEDED') {
            this.requestedAt = data.requestedAt || ''
            this.phase = 'sent'
            this.startPolling()
          } else {
            this.errorMessage = data.message || 'Resync ble ikke satt på serveren. Prøv igjen.'
          }
        },
        (error) => {
          this.saving = false
          this.errorMessage = error.response?.data?.message || 'Resync feilet. Prøv igjen.'
        }
      )
    },
    startPolling() {
      this.pollStartedAt = Date.now()
      this.pollTimer = setInterval(this.check, POLL_MS)
      this.check()
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    /** Reads what the terminal last reported and moves the dialog on; gives up with what it knows after 3 minutes. */
    check() {
      TerminalService.getTerminalSyncStates().then(
        (response) => {
          if (!this.pollTimer) return
          const terminals = response && response.data && Array.isArray(response.data.terminals) ? response.data.terminals : []
          this.evaluate(terminals.find((t) => t.terminalDocumentId === this.documentId))
        },
        () => {
          if (this.pollTimer) this.giveUpIfLate()
        }
      )
    },
    evaluate(state) {
      const deliveredAt = state ? Date.parse(state.resyncDeliveredAt || '') : NaN
      const requestedAt = Date.parse(this.requestedAt || '')
      // The flag set by THIS press: delivered after the server took it (SLACK covers whole-second rounding).
      const delivered = !isNaN(deliveredAt) && (isNaN(requestedAt) || deliveredAt >= requestedAt - SLACK_MS)
      if (delivered && this.phase === 'sent') {
        this.phase = 'delivered'
      }
      if (delivered && state && !state.reports) {
        this.phase = 'legacy'
        this.stopPolling()
        return
      }
      if (delivered && state && state.connected && state.resync === 'CONNECTED') {
        // The device's own resync, started on this delivery, has reached Sync Gateway.
        const resyncRequestedAt = Date.parse(state.resyncRequestedAt || '')
        if (!isNaN(resyncRequestedAt) && resyncRequestedAt >= deliveredAt - SLACK_MS) {
          this.phase = 'connected'
          this.stopPolling()
          return
        }
      }
      this.giveUpIfLate()
    },
    giveUpIfLate() {
      if (Date.now() - this.pollStartedAt < GIVE_UP_MS) return
      this.phase = this.phase === 'delivered' ? 'stuck' : 'undelivered'
      this.stopPolling()
    }
  }
}
</script>

<style scoped>
.btn-panel {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  border-top: var(--bo-muted-2) solid 1px;
  padding-top: 15px;
}

.btn-width {
  width: 80px;
  text-align: center;
}

.btn-wide {
  min-width: 120px;
  text-align: center;
}
</style>
